import ReactGA from 'react-ga4';
import { AssetTypes } from './enumerations';

const getCurrentDate = () => new Date().toISOString().split('T')[0];

const getAssetTypeName = (assetTypeId) => {
    if (assetTypeId === AssetTypes.Place) {
        return 'Asset';
    }

    if (assetTypeId === AssetTypes.Procedure) {
        return 'Procedure';
    }

    return 'Unknown';
};

const sendAnalyticsEvent = (eventName, payload = {}) => {
    try {
        ReactGA.event(eventName, {
            ...payload,
            current_date: getCurrentDate(),
        });
    } catch (error) {
        console.error(`Failed to send analytics event: ${eventName}`, error);
    }
};

const getMediaTypeFromPath = (srcPath = '', fallbackType = 'file') => {
    const lowerPath = String(srcPath || '').toLowerCase();

    if (/\.(mp4|webm|ogv|mov|m3u8)(\?|#|$)/.test(lowerPath)) return 'video';
    if (/\.(mp3|wav|ogg|m4a|aac)(\?|#|$)/.test(lowerPath)) return 'audio';
    if (/\.(png|jpg|jpeg|gif|webp|svg|bmp|avif)(\?|#|$)/.test(lowerPath)) return 'image';
    if (/\.(pdf)(\?|#|$)/.test(lowerPath)) return 'pdf';
    if (/\.(doc|docx|ppt|pptx|xls|xlsx|txt|rtf)(\?|#|$)/.test(lowerPath)) return 'document';

    return fallbackType;
};

export const trackPublishedJsonLoaded = (publishedData = {}) => {
    sendAnalyticsEvent('viewer_loaded', {
        asset_type: getAssetTypeName(publishedData.asset_type_id),
        display_name: publishedData.display_name || '',
        client_id: String(publishedData?.client?.client_id ?? ''),
        client_name: publishedData?.client?.display_name || '',
    });
};

export const trackActiveContextChanged = ({
    assetId,
    assetTypeId,
    displayName,
    zoneId,
    zoneName,
    clientId,
    clientName,
} = {}) => {
    sendAnalyticsEvent('viewer_context_changed', {
        asset_id: String(assetId ?? ''),
        asset_type: getAssetTypeName(assetTypeId),
        display_name: displayName || '',
        zone_id: String(zoneId ?? ''),
        zone_name: zoneName || '',
        client_id: String(clientId ?? ''),
        client_name: clientName || '',
    });
};

export const trackImageViewed = ({
    zoneId,
    zoneName,
    imageType,
    imageCount,
    imageSrcPath,
    assetId,
    assetTypeId,
    displayName,
    clientId,
    clientName,
} = {}) => {
    sendAnalyticsEvent('image_viewed', {
        zone_id: String(zoneId ?? ''),
        zone_name: zoneName || '',
        image_type: imageType || 'none',
        image_count: Number(imageCount ?? 0),
        image_src_path: imageSrcPath || '',
        asset_id: String(assetId ?? ''),
        asset_type: getAssetTypeName(assetTypeId),
        display_name: displayName || '',
        client_id: String(clientId ?? ''),
        client_name: clientName || '',
    });
};

export const trackContentViewed = ({ contentType, srcPath, procedureId, procedureName } = {}) => {
    sendAnalyticsEvent('content_viewed', {
        type: getMediaTypeFromPath(srcPath, contentType || 'file'),
        src_path: srcPath || '',
        procedure_id: String(procedureId ?? ''),
        procedure_name: procedureName || '',
    });
};

export const trackContentLinkViewed = ({ contentType, srcPath, assetId, assetName } = {}) => {
    sendAnalyticsEvent('content_link_viewed', {
        type: getMediaTypeFromPath(srcPath, contentType || 'file'),
        src_path: srcPath || '',
        asset_id: String(assetId ?? ''),
        asset_name: assetName || '',
    });
};
